// Authord.by.KWShin.2024.03.14.
import { format } from 'date-fns';
import { api } from '@/api/common/instances';
import { comma, kilobytes } from '@/utils/formatter';
import motletable from '@/mixins/motletable';
import chartable from '@/mixins/chartable';
import BarChart from '../../../components/BarChartNeo';

export default {
  components: {
    BarChart,
  },
  mixins: [motletable, chartable],
  props: {
    sstmId: {
      type: String,
      required: true,
    },
    storeName: {
      type: String,
      required: true,
    },
    timestamp: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      data: [],
      motletOptions: {
        monitor: {
          systemId: this.sstmId,
        },
        descendant: {
          systemId: this.sstmId,
        },
      },
      // BEGIN::Added.by.KWShin.2024.03.13.
      datetime: null,
      // FINISH::Added.by.KWShin.2024.03.13.
    };
  },
  computed: {
    descendants() {
      return this.$store.getters[`${this.storeName}/get`];
    },
    categoryField() {
      return this.motletState.monitor.categoryField;
    },
  },
  /**
   * Commented.by.KWShin.2024.03.14.
   * 시스템이 바뀌거나 수집주기 상의 시각이 바뀌면 데이터를 새로 읽어온다.
   * 시스템이 바뀌는 이벤트가 발생되면 곧바로 부모 컴포넌트로부터 timestamp가 바뀌지만,
   * 가끔 가다가 미처 timestamp가 설정되기 전에 시스템 변동 이벤트를 이 컴포넌트에서
   * 먼저 인식하는 경우가 있다.
   *
   * Data를 읽어오게 되면,
   * 차트에서 필요로 하는 options 정보를 새로 읽어온 데이터에 기반하여 재생성한다.
   * Instance 선택이 변경되었을 때에는 기존의 Data에 기반하여 options 데이터를 재성성한다.
   */
  watch: {
    // BEGIN::Added.by.KWShin.2024.03.13.
    sstmId: {
      handler() {
        if (this.datetime) {
          this.fetchData(this.datetime);
        } else {
          setTimeout(() => {
            this.fetchData(this.datetime);
          }, 200);
        }
      },
    },
    // FINISH::Added.by.KWShin.2024.03.13.
    timestamp(value) {
      this.datetime = value;
      this.fetchData(value);
    },
    // BEGIN::Added.by.KWShin.2024.03.14.
    data: {
      handler() {
        this.setOptions();
      },
    },
    descendants: {
      deep: true,
      handler() {
        this.setOptions();
      },
    },
    // FINISH::Added.by.KWShin.2024.03.14.
  },
  methods: {
    setOptions() {
      if (this.data.length == 0) return {};
      const percent = this.chartField.type == 'PERCENT';
      // data 내의 모든 categoryField 를 취합하여 중복제거 & 정렬.
      const categories = [...new Set(this.data.reduce((acc, item) => acc.concat(item.data.map(s => s[this.categoryField])), []))].sort();
      const xAxis = (() => {
        return { data: categories };
      })();
      const yAxis = {
        name: `${this.chartField.text} ${percent ? '(%)' : ''}`,
        max: percent ? 100 : undefined,
        maxInterval: percent ? 25 : undefined,
        splitNumber: percent ? 4 : undefined,
      };
      const series = this.descendants
        .filter(inst => inst.active)
        .map(inst => {
          const data = categories.map(cate => {
            const instData = this.data.find(datum => datum.id == inst.id);
            if (instData) {
              const cateData = instData.data.find(instDatum => instDatum.fsysname == cate);
              if (cateData) {
                return cateData.usedRate;
              } else {
                /*
                 * Commented.by.KWShin.2024.03.14.
                 * 데이터가 없는 경우 반드시 0을 리턴해주어야 한다.
                 * 그렇지 않으면, 차트에서 해당 데이터의 위치에 이전에 표시되었던 데이터가 계속 남아서 표시된다.
                 */
                return 0;
              }
            } else {
              /*
               * Commented.by.KWShin.2024.03.14.
               * 데이터가 없는 경우 반드시 0을 리턴해주어야 한다.
               * 그렇지 않으면, 차트에서 해당 데이터의 위치에 이전에 표시되었던 데이터가 계속 남아서 표시된다.
               */
              return 0;
            }
          });
          return {
            name: inst.id,
            type: 'bar',
            itemStyle: {
              color: inst.color,
            },
            data,
          };
        });
      const valueFormatter = (data, type = this.chartField.type) => {
        const formatted = {
          unit: '',
          data: '',
        };
        const isEmpty = data === undefined || data === null || data === '';
        data = isEmpty ? '' : data;
        if (type == 'PERCENT') {
          formatted.unit = isEmpty ? '' : ' %';
          formatted.data = data;
        } else if (type == 'DIGIT') {
          formatted.data = comma(data);
        } else if (type == 'KILO_BYTE') {
          formatted.data = kilobytes(data);
        } else {
          formatted.data = data;
        }
        return `${formatted.data}${formatted.unit}`;
      };
      const formatter = {
        tooltip: params => {
          const contents = [];
          contents.push('<i class="v-icon notranslate mdi mdi-clock-time-four-outline mr-1" style="font-size: 12px; line-height: 10px"></i>');
          contents.push(format(this.datetime, 'yyyy-MM-dd HH:mm:ss'));
          contents.push('<br />');
          contents.push('<i class="v-icon notranslate mdi mdi-harddisk mr-1" style="font-size: 12px; line-height: 10px"></i>');
          contents.push(params[0].name);
          contents.push('<br />');

          params.forEach(p => {
            const color = this.descendants.find(d => d.id == p.seriesName).color;
            contents.push(this.circle(color));
            contents.push(`<span style="color:${color};font-weight:900;">`);
            contents.push(this.descendants.find(d => d.id == p.seriesName).shortAlias);
            contents.push('</span> : ');
            contents.push(valueFormatter(p.data));
            contents.push('<br />');
          });

          return contents.join('');
        },
      };
      const options = {
        xAxis,
        yAxis,
        series,
        tooltip: {
          formatter: formatter.tooltip,
        },
      };
      this.$refs.barChart.setOptions(options);
    },
    async fetchData(timestamp) {
      const sstmId = this.sstmId;
      const motletCode = this.monitor.monitorCode;
      const params = {
        endYmdhms: timestamp,
        sstmId,
        motletCode,
      };
      const rslt = await api.get('/motlet-data/chart', { params });
      const { data: results } = rslt;
      this.data = results.map(result => {
        const {
          key: { instId: id },
          data,
        } = result;
        return { id, data };
      });
    },
  },
};
